import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FiHeart, FiChevronDown, FiChevronUp, FiZap, FiAlertTriangle,
  FiAward, FiActivity, FiUsers, FiCalendar, FiCpu,
} from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';
import pulseService from '../services/pulseService';

// ── Tier config ────────────────────────────────────────────────────────────
const TIER_STYLE: Record<string, { border: string; badge: string; label: string }> = {
  brief:    { border: 'border-l-gray-500',   badge: 'bg-gray-700/60 text-gray-300',       label: 'Brief'    },
  standard: { border: 'border-l-indigo-500', badge: 'bg-indigo-900/60 text-indigo-300',   label: 'Standard' },
  detailed: { border: 'border-l-purple-500', badge: 'bg-purple-900/60 text-purple-300',   label: 'Detailed' },
};
const tierStyle = (tier: string) => TIER_STYLE[tier] ?? TIER_STYLE.standard;

// ── Snapshot chip helper ───────────────────────────────────────────────────
function Chip({ icon, value, label, color }: { icon: React.ReactNode; value: React.ReactNode; label: string; color: string }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${color}`}>
      {icon}<span className="font-semibold">{value}</span><span className="opacity-70">{label}</span>
    </span>
  );
}

// ── Snapshot mini-dashboard ────────────────────────────────────────────────
function SnapshotPanel({ snapshot, darkMode }: { snapshot: any; darkMode: boolean }) {
  if (!snapshot || Object.keys(snapshot).length === 0) return null;
  const bg = darkMode ? 'bg-gray-900/60 border-gray-700/60' : 'bg-gray-50 border-gray-200';

  const ws = snapshot.work_state ?? {};
  const ev = snapshot.events ?? {};
  const at = snapshot.attention_items ?? {};
  const wi = snapshot.wins ?? {};
  const ua = snapshot.user_activity ?? {};
  const gym = snapshot.gymnasium ?? {};

  const cells = [
    { icon: <FiActivity className="w-3 h-3" />, value: ws.health_score != null ? `${ws.health_score}/100` : '—', label: 'Health', color: 'text-emerald-400' },
    { icon: <FiZap className="w-3 h-3" />, value: ev.total ?? '—', label: 'Events', color: 'text-blue-400' },
    { icon: <FiAlertTriangle className="w-3 h-3" />, value: at.count ?? (at.items?.length ?? '—'), label: 'Attention', color: 'text-amber-400' },
    { icon: <FiAward className="w-3 h-3" />, value: wi.count ?? (wi.items?.length ?? '—'), label: 'Wins', color: 'text-green-400' },
    { icon: <FiUsers className="w-3 h-3" />, value: ua.active_users ?? '—', label: 'Active users', color: 'text-purple-400' },
    { icon: <FiCpu className="w-3 h-3" />, value: gym.total ?? '—', label: 'Arena matches', color: 'text-cyan-400' },
    { icon: <FiCalendar className="w-3 h-3" />, value: snapshot.session_count ?? '—', label: 'Sessions', color: 'text-indigo-400' },
  ].filter(c => c.value !== '—' && c.value !== 0);

  if (cells.length === 0) return null;

  return (
    <div className={`mt-4 rounded-lg border p-3 ${bg}`}>
      <p className={`text-[10px] font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        Data Snapshot
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {cells.map(c => (
          <div key={c.label} className={`flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <span className={c.color}>{c.icon}</span>
            <div>
              <p className="text-sm font-semibold leading-none">{c.value}</p>
              <p className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{c.label}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
const PulseHistory: React.FC = () => {
  const { darkMode } = useTheme();
  const [searchParams] = useSearchParams();
  const expandId = searchParams.get('expand') ?? undefined;
  const [limit, setLimit] = useState(10);
  const [expanded, setExpanded] = useState<Record<string, boolean>>(
    expandId ? { [expandId]: true } : {}
  );
  const [artifactCache, setArtifactCache] = useState<Record<string, any>>({});
  const expandRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['pulse', 'history', 'page', limit],
    queryFn: () => pulseService.getHistory({ type: 'heartbeat', limit }),
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
  });

  const items: any[] = data?.items ?? [];
  const total: number = data?.total ?? 0;
  const hasMore = items.length < total;

  // Cumulative stats from loaded items
  const { totalTokens, totalCost } = useMemo(() => {
    const tokens = items.reduce((s, i) => s + (i.token_usage?.total_tokens ?? 0), 0);
    const cost = items.reduce((s, i) => s + (i.token_usage?.estimated_cost_usd ?? 0), 0);
    return { totalTokens: tokens, totalCost: cost };
  }, [items]);

  const latestHealth = items[0]?.snapshot_summary?.health_score ?? null;

  // Deep-link: fetch artifact + scroll on mount
  useEffect(() => {
    if (!expandId) return;
    pulseService.getArtifact(expandId)
      .then(artifact => setArtifactCache(prev => ({ ...prev, [expandId]: artifact })))
      .catch(() => {});
  }, [expandId]);

  useEffect(() => {
    if (!expandId || !expandRef.current) return;
    setTimeout(() => expandRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  }, [expandId, expandRef.current]);

  const toggleExpand = async (id: string) => {
    const next = !expanded[id];
    setExpanded(prev => ({ ...prev, [id]: next }));
    if (next && !artifactCache[id]) {
      try {
        const artifact = await pulseService.getArtifact(id);
        setArtifactCache(prev => ({ ...prev, [id]: artifact }));
      } catch {}
    }
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const muted = darkMode ? 'text-gray-500' : 'text-gray-400';

  return (
    <div className={`min-h-screen p-6 space-y-6 ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${darkMode ? 'bg-indigo-900/40' : 'bg-indigo-100'}`}>
            <FiHeart
              className="w-5 h-5 text-indigo-400"
              style={{ animation: 'breathe 2s ease-in-out infinite' }}
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-indigo-300 bg-clip-text text-transparent">
              Heartbeat History
            </h1>
            <p className={`text-xs mt-0.5 ${muted}`}>
              Daily briefings narrated by the Pulse Engine · scheduled 6 AM
            </p>
          </div>
        </div>
        {latestHealth != null && (
          <div className={`text-right shrink-0 px-3 py-2 rounded-lg border ${darkMode ? 'border-indigo-900/40 bg-indigo-900/20' : 'border-indigo-100 bg-indigo-50'}`}>
            <p className={`text-[10px] uppercase tracking-wider ${muted}`}>Latest health</p>
            <p className="text-xl font-bold text-indigo-400">{latestHealth}<span className="text-xs">/100</span></p>
          </div>
        )}
      </div>

      {/* ── Stats bar ─────────────────────────────────────────────────────── */}
      {items.length > 0 && (
        <div className={`grid grid-cols-3 gap-3`}>
          {[
            { icon: <FiHeart className="w-4 h-4 text-indigo-400" />, value: `${items.length}${total > items.length ? ` of ${total}` : ''}`, label: 'Briefings' },
            { icon: <FiZap className="w-4 h-4 text-amber-400" />, value: totalTokens.toLocaleString(), label: 'Tokens consumed' },
            { icon: <span className="text-sm text-emerald-400 font-mono">$</span>, value: `$${totalCost.toFixed(4)}`, label: 'Estimated cost' },
          ].map(s => (
            <div key={s.label} className={`rounded-lg border p-3 flex items-center gap-3 ${darkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700/60' : 'bg-gray-50'}`}>{s.icon}</div>
              <div>
                <p className="text-base font-semibold leading-tight">{s.value}</p>
                <p className={`text-[10px] ${muted}`}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Loading skeletons ─────────────────────────────────────────────── */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className={`rounded-lg border border-l-4 border-l-indigo-500 p-4 animate-pulse ${darkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`h-4 w-2/5 rounded mb-3 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} />
              <div className={`h-3 w-full rounded mb-1.5 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} />
              <div className={`h-3 w-4/5 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} />
            </div>
          ))}
        </div>
      )}

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {isError && (
        <div className={`rounded-lg border border-red-500/30 p-4 text-sm text-red-400 ${darkMode ? 'bg-red-900/10' : 'bg-red-50'}`}>
          Failed to load briefing history.
        </div>
      )}

      {/* ── Empty ─────────────────────────────────────────────────────────── */}
      {!isLoading && !isError && items.length === 0 && (
        <div className={`rounded-lg border p-10 text-center ${darkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${darkMode ? 'bg-indigo-900/40' : 'bg-indigo-100'}`}>
            <FiHeart className="w-6 h-6 text-indigo-400" />
          </div>
          <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>No briefings yet</p>
          <p className={`text-xs ${muted}`}>The first pulse generates daily at 6 AM, or run one now via Admin Chat.</p>
        </div>
      )}

      {/* ── Briefing cards ────────────────────────────────────────────────── */}
      <div className="space-y-3">
        {items.map((item: any) => {
          const id = item.artifact_id;
          const isOpen = !!expanded[id];
          const artifact = artifactCache[id];
          const ts = tierStyle(item.tier ?? 'standard');
          const generatedAt = item.generated_at ? new Date(item.generated_at) : null;
          const dateStr = generatedAt?.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
          const timeStr = generatedAt?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          const tokens = item.token_usage?.total_tokens;
          const cost = item.token_usage?.estimated_cost_usd;
          const ss = item.snapshot_summary;
          const modelShort = (item.model ?? '').split('/').pop()?.replace(/-\d{8}$/, '') ?? '';

          return (
            <div
              key={id}
              ref={id === expandId ? expandRef : null}
              className={`rounded-lg border border-l-4 ${ts.border} overflow-hidden transition-shadow hover:shadow-md ${
                darkMode ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-200'
              }`}
            >
              {/* ── Collapsed header (always visible) ── */}
              <button
                className="w-full text-left p-4 flex items-start justify-between gap-3"
                onClick={() => toggleExpand(id)}
              >
                <div className="min-w-0 flex-1 space-y-2">
                  {/* Row 1: Title + date */}
                  <div className="flex items-start justify-between gap-2">
                    <span className={`text-sm font-semibold leading-snug ${darkMode ? 'text-indigo-200' : 'text-indigo-800'}`}>
                      {item.name ?? 'Daily Briefing'}
                    </span>
                    <span className={`text-[10px] shrink-0 pt-0.5 ${muted}`}>{dateStr} · {timeStr}</span>
                  </div>

                  {/* Row 2: Tier / model / token metadata */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${ts.badge}`}>{ts.label}</span>
                    {modelShort && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                        {modelShort}
                      </span>
                    )}
                    {tokens != null && (
                      <span className={`text-[10px] ${muted}`}>{tokens.toLocaleString()} tok</span>
                    )}
                    {cost != null && (
                      <span className={`text-[10px] ${muted}`}>· ${cost.toFixed(4)}</span>
                    )}
                    {item.zero_data && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>
                        no data
                      </span>
                    )}
                  </div>

                  {/* Row 3: Snapshot chips */}
                  {ss && (
                    <div className="flex flex-wrap gap-1.5">
                      {ss.health_score != null && (
                        <Chip icon={<FiActivity className="w-3 h-3" />} value={`${ss.health_score}/100`} label="health" color={darkMode ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-50 text-emerald-700'} />
                      )}
                      {ss.event_count != null && ss.event_count > 0 && (
                        <Chip icon={<FiZap className="w-3 h-3" />} value={ss.event_count} label="events" color={darkMode ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-50 text-blue-700'} />
                      )}
                      {ss.attention_count != null && ss.attention_count > 0 && (
                        <Chip icon={<FiAlertTriangle className="w-3 h-3" />} value={ss.attention_count} label="attention" color={darkMode ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-50 text-amber-700'} />
                      )}
                      {ss.wins_count != null && ss.wins_count > 0 && (
                        <Chip icon={<FiAward className="w-3 h-3" />} value={ss.wins_count} label="wins" color={darkMode ? 'bg-green-900/40 text-green-400' : 'bg-green-50 text-green-700'} />
                      )}
                      {ss.active_users != null && ss.active_users > 0 && (
                        <Chip icon={<FiUsers className="w-3 h-3" />} value={ss.active_users} label="active users" color={darkMode ? 'bg-purple-900/40 text-purple-400' : 'bg-purple-50 text-purple-700'} />
                      )}
                    </div>
                  )}

                  {/* Row 4: Preview text (collapsed only) */}
                  {!isOpen && item.content_preview && (
                    <p className={`text-xs leading-relaxed line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {item.content_preview}
                    </p>
                  )}
                </div>

                <div className="shrink-0 mt-0.5">
                  {isOpen
                    ? <FiChevronUp className={`w-4 h-4 ${muted}`} />
                    : <FiChevronDown className={`w-4 h-4 ${muted}`} />
                  }
                </div>
              </button>

              {/* ── Expanded content ── */}
              {isOpen && (
                <div className={`border-t px-4 pb-5 ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                  {!artifact ? (
                    <div className={`py-8 text-center text-xs ${muted}`}>Loading briefing…</div>
                  ) : (
                    <>
                      {/* Date range strip */}
                      {artifact.date_range?.start && (
                        <p className={`text-[10px] pt-3 pb-1 ${muted}`}>
                          Period: {new Date(artifact.date_range.start).toLocaleDateString([], { month: 'short', day: 'numeric' })} → {new Date(artifact.date_range.end).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      )}

                      {/* Full briefing prose — strip H1 if it duplicates the card header */}
                      <div className={`prose prose-sm max-w-none pt-2 ${darkMode ? 'prose-invert' : ''}`}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            // Pulse content is LLM-generated — links should
                            // never navigate away from the pulse history page.
                            a: ({ node, href, children, ...props }) => (
                              <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {(() => {
                            const raw = artifact.full_content ?? '';
                            const lines = raw.split('\n');
                            if (lines.length > 0 && lines[0].startsWith('#')) {
                              const headingText = lines[0].replace(/^#+\s*/, '').trim();
                              if (item.name?.startsWith(headingText)) {
                                return lines.slice(1).join('\n').trimStart();
                              }
                            }
                            return raw;
                          })()}
                        </ReactMarkdown>
                      </div>

                      {/* Snapshot mini-dashboard */}
                      <SnapshotPanel snapshot={artifact.snapshot} darkMode={darkMode} />
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Load more ─────────────────────────────────────────────────────── */}
      {hasMore && !isLoading && (
        <div className="flex justify-center pt-2">
          <button
            onClick={() => setLimit(l => l + 10)}
            className={`text-sm px-5 py-2 rounded-lg border transition-colors ${
              darkMode
                ? 'border-gray-700 text-gray-400 hover:text-indigo-400 hover:border-indigo-500/40 hover:bg-indigo-900/10'
                : 'border-gray-200 text-gray-600 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50'
            }`}
          >
            Load {Math.min(10, total - items.length)} more
          </button>
        </div>
      )}

      {/* breathe keyframe now in global CSS (index.css) */}
    </div>
  );
};

export default PulseHistory;
